import React, { useState } from 'react';
import { Cloud, Plus, Shield, Zap, CheckCircle, XCircle, AlertCircle, Loader2, ExternalLink, AlertTriangle } from 'lucide-react';
import { apiService } from '../../services/api';

interface ZoneCreationResult {
  domain: string;
  success: boolean;
  response?: any;
  error?: string;
}

export const CreateZone: React.FC = () => {
  const [domainsText, setDomainsText] = useState('');
  const [planType, setPlanType] = useState('free');
  const [isCreating, setIsCreating] = useState(false);
  const [results, setResults] = useState<ZoneCreationResult[]>([]);
  const [showResults, setShowResults] = useState(false);

  // Parse domains from the text area
  const getDomainsList = () => {
    return domainsText
      .split('\n')
      .map(domain => domain.trim())
      .filter(domain => domain.length > 0);
  };

  // Create zone for a single domain
  const createZoneForDomain = async (domain: string): Promise<ZoneCreationResult> => {
    try {
      // Call our backend proxy endpoint instead of external API directly
      const result = await apiService.createZone(domain);
      
      if (result.success) {
        return {
          domain,
          success: true,
          response: result.response
        };
      } else {
        return {
          domain,
          success: false,
          error: result.error || result.message,
          response: result.response
        };
      }
    } catch (error) {
      return {
        domain,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  };

  const handleCreateZones = async () => {
    const domains = getDomainsList();
    if (domains.length === 0) return;

    setIsCreating(true);
    setResults([]);
    setShowResults(true);

    const newResults: ZoneCreationResult[] = [];

    try {
      // Create a function to process domains with controlled concurrency
      const processDomainsConcurrently = async (domainList: string[], maxConcurrency = 3) => {
        const results: ZoneCreationResult[] = [];
        const executing: Promise<void>[] = [];

        for (let i = 0; i < domainList.length; i++) {
          const domain = domainList[i];
          
          const promise = createZoneForDomain(domain).then(result => {
            results.push(result);
            // Update results in real-time as each domain completes
            setResults([...results]);
          });

          executing.push(promise);

          // If we've reached max concurrency or this is the last domain, wait for some to complete
          if (executing.length >= maxConcurrency || i === domainList.length - 1) {
            await Promise.allSettled(executing);
            executing.length = 0; // Clear the array
          }
        }

        return results;
      };

      // Process domains with max 3 concurrent requests
      await processDomainsConcurrently(domains, 3);

    } catch (error) {
      console.error('Error in batch zone creation:', error);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Processing Warning Banner */}
      {isCreating && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-white px-4 py-3 shadow-lg">
          <div className="flex items-center justify-center space-x-3 max-w-6xl mx-auto">
            <AlertTriangle className="h-5 w-5 flex-shrink-0" />
            <div className="flex items-center space-x-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="font-medium">
                Creating zones for {getDomainsList().length} domain(s)...
              </span>
            </div>
            <span className="text-sm opacity-90">
              Please do not refresh or leave this page
            </span>
          </div>
        </div>
      )}

      {/* Main Content - Add top padding when banner is showing */}
      <div className={`${isCreating ? 'pt-16' : ''} transition-all duration-300`}>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Create Zone on Cloudflare</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Create a new DNS zone on Cloudflare for domain management
          </p>
        </div>

        <div className="card-modern p-6 space-y-6">
          <div>
            <h2 className="text-lg font-semibold mb-4">Zone Details</h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Domain Names
                </label>
                <textarea
                  value={domainsText}
                  onChange={(e) => setDomainsText(e.target.value)}
                  className="input-modern w-full px-3 py-2 text-sm min-h-[120px] resize-vertical"
                  placeholder="Enter domain names, one per line:&#10;example.com&#10;mysite.org&#10;anotherdomain.net"
                  rows={6}
                  disabled={isCreating}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Enter root domains without www or subdomains, one per line. You can copy and paste multiple domains.
                </p>
                {domainsText && (
                  <p className="text-xs text-blue-600 mt-1">
                    {getDomainsList().length} domain(s) will be processed
                  </p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Plan Type
                </label>
                <div className="grid gap-3 md:grid-cols-3">
                  <label className={`card-modern p-4 cursor-pointer transition-all ${
                    planType === 'free' ? 'ring-2 ring-primary' : ''
                  }`}>
                    <input
                      type="radio"
                      name="plan"
                      value="free"
                      checked={planType === 'free'}
                      onChange={(e) => setPlanType(e.target.value)}
                      className="sr-only"
                      disabled={isCreating}
                    />
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Cloud className="h-4 w-4 text-primary" />
                        <span className="font-medium">Free</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Basic features, DDoS protection
                      </p>
                    </div>
                  </label>

                  <label className="card-modern p-4 cursor-not-allowed opacity-50 transition-all bg-muted/50">
                    <input
                      type="radio"
                      name="plan"
                      value="pro"
                      disabled
                      className="sr-only"
                    />
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Zap className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-muted-foreground">Pro</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Not available
                      </p>
                    </div>
                  </label>

                  <label className="card-modern p-4 cursor-not-allowed opacity-50 transition-all bg-muted/50">
                    <input
                      type="radio"
                      name="plan"
                      value="business"
                      disabled
                      className="sr-only"
                    />
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Shield className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-muted-foreground">Business</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Not available
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-border pt-6">
            <h2 className="text-lg font-semibold mb-4">Zone Configuration</h2>
            <div className="space-y-3 opacity-50">
              <label className="flex items-center space-x-3 cursor-not-allowed">
                <input type="checkbox" className="rounded border-border" defaultChecked disabled />
                <span className="text-sm text-muted-foreground">Enable automatic HTTPS rewrites</span>
              </label>
              <label className="flex items-center space-x-3 cursor-not-allowed">
                <input type="checkbox" className="rounded border-border" defaultChecked disabled />
                <span className="text-sm text-muted-foreground">Enable Always Use HTTPS</span>
              </label>
              <label className="flex items-center space-x-3 cursor-not-allowed">
                <input type="checkbox" className="rounded border-border" defaultChecked disabled />
                <span className="text-sm text-muted-foreground">Enable Auto Minify (JS, CSS, HTML)</span>
              </label>
              <label className="flex items-center space-x-3 cursor-not-allowed">
                <input type="checkbox" className="rounded border-border" disabled />
                <span className="text-sm text-muted-foreground">Enable Development Mode</span>
              </label>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Zone configuration options are automatically applied
            </p>
          </div>

          <div className="flex items-center justify-end pt-4">
            <button 
              onClick={handleCreateZones}
              disabled={!domainsText.trim() || isCreating}
              className="btn-primary px-6 py-2 rounded-lg font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              <span>
                {isCreating 
                  ? 'Creating...' 
                  : `Create Zone${getDomainsList().length > 1 ? 's' : ''}`
                }
              </span>
            </button>
          </div>
        </div>

        {/* Results Section */}
        {showResults && (
          <div className="card-modern p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Creation Results</h2>
              <button
                onClick={() => setShowResults(false)}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Hide Results
              </button>
            </div>
            
            {results.length === 0 && isCreating && (
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Processing domains...</span>
              </div>
            )}
            
            <div className="space-y-4">
              {results.map((result, index) => {
                // Parse the nested response structure
                const responseBody = result.response?.body;
                const responseData = responseBody?.data;
                const isExisting = responseBody?.isExisting;
                const apiSuccess = responseBody?.success;
                const apiMessage = responseBody?.message;
                
                return (
                  <div key={index} className="border border-border rounded-lg p-4">
                    <div className="space-y-3">
                      {/* Domain Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3">
                          {result.success ? (
                            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                          ) : (
                            <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                          )}
                          <div>
                            <p className="font-medium text-lg">{result.domain}</p>
                            <p className={`text-sm ${result.success ? 'text-green-600' : 'text-red-600'}`}>
                              {result.success 
                                ? (apiMessage || 'Zone processed successfully') 
                                : result.error
                              }
                            </p>
                            {isExisting && (
                              <span className="inline-flex items-center rounded-full bg-blue-500/10 px-2 py-0.5 text-xs font-medium text-blue-500 mt-1">
                                Existing Zone
                              </span>
                            )}
                          </div>
                        </div>
                        {result.response && (
                          <button
                            onClick={() => {
                              const popup = window.open('', '_blank', 'width=800,height=600,scrollbars=yes');
                              if (popup) {
                                popup.document.write(`
                                  <html>
                                    <head>
                                      <title>Full API Response - ${result.domain}</title>
                                      <style>
                                        body { font-family: monospace; margin: 20px; background: #f5f5f5; }
                                        pre { background: white; padding: 15px; border-radius: 5px; overflow: auto; }
                                        h3 { color: #333; margin-top: 0; }
                                      </style>
                                    </head>
                                    <body>
                                      <h3>Full API Response for ${result.domain}</h3>
                                      <pre>${JSON.stringify(result.response, null, 2)}</pre>
                                    </body>
                                  </html>
                                `);
                              }
                            }}
                            className="text-sm text-primary hover:text-primary/80 flex items-center space-x-1"
                          >
                            <ExternalLink className="h-3 w-3" />
                            <span>Full Response</span>
                          </button>
                        )}
                      </div>

                      {/* Detailed Information */}
                      {result.success && responseData && (
                        <div className="ml-8 space-y-2 text-sm">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {responseData.id && (
                              <div>
                                <span className="font-medium text-muted-foreground">Zone ID:</span>
                                <p className="font-mono text-xs bg-muted px-2 py-1 rounded mt-1">
                                  {responseData.id}
                                </p>
                              </div>
                            )}
                            
                            {responseData.cloudflareZoneId && (
                              <div>
                                <span className="font-medium text-muted-foreground">Cloudflare Zone ID:</span>
                                <p className="font-mono text-xs bg-muted px-2 py-1 rounded mt-1">
                                  {responseData.cloudflareZoneId}
                                </p>
                              </div>
                            )}
                            
                            {responseData.status && (
                              <div>
                                <span className="font-medium text-muted-foreground">Status:</span>
                                <span className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                  responseData.status === 'active' 
                                    ? 'bg-green-500/10 text-green-500' 
                                    : 'bg-yellow-500/10 text-yellow-500'
                                }`}>
                                  {responseData.status}
                                </span>
                              </div>
                            )}
                            
                            {responseData.redirectUrl && (
                              <div>
                                <span className="font-medium text-muted-foreground">Redirect URL:</span>
                                <p className="text-blue-600 truncate mt-1">{responseData.redirectUrl}</p>
                              </div>
                            )}
                          </div>

                          {/* Nameservers */}
                          {responseData.nameservers && responseData.nameservers.length > 0 && (
                            <div className="mt-3">
                              <span className="font-medium text-muted-foreground">Nameservers:</span>
                              <div className="mt-1 space-y-1">
                                {responseData.nameservers.map((ns: string, nsIndex: number) => (
                                  <p key={nsIndex} className="font-mono text-xs bg-muted px-2 py-1 rounded">
                                    {ns}
                                  </p>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Error Message (if any) */}
                          {responseData.errorMessage && (
                            <div className="mt-3">
                              <span className="font-medium text-muted-foreground">Note:</span>
                              <p className="text-amber-600 text-xs mt-1">{responseData.errorMessage}</p>
                            </div>
                          )}

                          {/* Timestamps */}
                          <div className="mt-3 pt-2 border-t border-border">
                            <div className="flex justify-between text-xs text-muted-foreground">
                              {responseData.createdAt && (
                                <span>Created: {new Date(responseData.createdAt).toLocaleString()}</span>
                              )}
                              {responseData.updatedAt && (
                                <span>Updated: {new Date(responseData.updatedAt).toLocaleString()}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {results.length > 0 && !isCreating && (
              <div className="mt-4 p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 text-sm">
                    <div className="flex items-center space-x-1">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>{results.filter(r => r.success).length} successful</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <XCircle className="h-4 w-4 text-red-500" />
                      <span>{results.filter(r => !r.success).length} failed</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <AlertCircle className="h-4 w-4 text-blue-500" />
                      <span>
                        {results.filter(r => r.response?.body?.isExisting).length} existing zones
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      const allResults = results.map(r => ({
                        domain: r.domain,
                        success: r.success,
                        response: r.response,
                        error: r.error
                      }));
                      const popup = window.open('', '_blank', 'width=900,height=700,scrollbars=yes');
                      if (popup) {
                        popup.document.write(`
                          <html>
                            <head>
                              <title>All API Responses - Zone Creation</title>
                              <style>
                                body { font-family: monospace; margin: 20px; background: #f5f5f5; }
                                pre { background: white; padding: 15px; border-radius: 5px; overflow: auto; margin-bottom: 20px; }
                                h3 { color: #333; margin-top: 0; }
                                .domain-section { margin-bottom: 30px; border-bottom: 2px solid #ddd; padding-bottom: 20px; }
                              </style>
                            </head>
                            <body>
                              <h2>All API Responses - Zone Creation Results</h2>
                              ${allResults.map(result => `
                                <div class="domain-section">
                                  <h3>${result.domain} ${result.success ? '✅' : '❌'}</h3>
                                  <pre>${JSON.stringify(result, null, 2)}</pre>
                                </div>
                              `).join('')}
                            </body>
                          </html>
                        `);
                      }
                    }}
                    className="text-sm text-primary hover:text-primary/80 flex items-center space-x-1"
                  >
                    <ExternalLink className="h-3 w-3" />
                    <span>Export All Results</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="card-modern p-4 bg-green-500/10 border border-green-500/20">
          <p className="text-sm text-green-600 dark:text-green-400">
            <strong>Next Steps:</strong> After creating the zone{getDomainsList().length > 1 ? 's' : ''}, update your domain{getDomainsList().length > 1 ? 's' : ''} nameservers at your registrar to point to Cloudflare.
          </p>
        </div>
      </div>
    </div>
  );
}; 